import { Box, Card, CardContent, Grid, Stack, SvgIcon, Typography } from "@mui/material";
import { Error } from "@common/HandleErrorAndLoading/Error";
import { useTranslation } from "react-i18next";
import { useStyle } from "./style";
import { useEmail } from "@features/email/useEmail";
import { emailRegex, noSpacesRegex } from "@utils/formControls";
import { Controller } from "react-hook-form";
import { LabeledTextField } from "@common/LabeledTextField/LabeledTextField";
import { LabeledButton } from "@common/LabeledButton/LabeledButton";
import { BRAND_NAME } from "@constants";
import CheckIcon from "@static/icons/Check.svg";
import { FONT_WEIGHTS } from "../../theme/fontWeights";
import { useCallback } from "react";

interface IParams {
    eircode: string;
    isWrapped?: boolean;
    persistBroadbandEmailSent?: boolean;
}

export const Email = (props: IParams) => {
    const { eircode, isWrapped, persistBroadbandEmailSent } = props;
    const { t } = useTranslation(["home", "validation", "customerDetails"]);
    const { classes } = useStyle({ isWrapped });

    const { formMethods, isLoading, error, isEmailSentSucceed, submitEmail } = useEmail({
        eircode,
        persistBroadbandEmailSent,
    });

    const renderSuccessContent = useCallback(
        () => (
            <Stack alignItems="center" padding={1} spacing={1}>
                <SvgIcon className={classes.successIcon} color="primary" component={CheckIcon} inheritViewBox />
                <Typography align="center" color="secondary" fontWeight={FONT_WEIGHTS.default} variant="large">
                    {t("home:detailsSavedTitle")}
                </Typography>
                <Typography align="center" color="secondary" variant="normal">
                    {t("home:detailsSavedDescription", { brand: BRAND_NAME })}
                </Typography>
            </Stack>
        ),
        [classes.successIcon, t]
    );

    const renderFormContent = useCallback(
        () => (
            <form onSubmit={submitEmail}>
                <Grid alignItems="center" columnSpacing={2} container paddingInline={1}>
                    <Grid item xs={isWrapped ? 12 : 8.5}>
                        <Controller
                            control={formMethods.control}
                            name="email"
                            render={(renderProps) => (
                                <LabeledTextField
                                    error={Boolean(formMethods.errors.email)}
                                    fullWidth
                                    helperText={formMethods.errors.email?.message}
                                    id="email"
                                    inputRef={renderProps.ref}
                                    isRequired
                                    labelText={t("customerDetails:emailAddress")}
                                    name={renderProps.name}
                                    placeholder="email@gmail.com"
                                    value={renderProps.value}
                                    onChange={renderProps.onChange}
                                />
                            )}
                            rules={{
                                required: t("validation:required"),
                                validate: (value: string) => noSpacesRegex(value) ?? emailRegex(value),
                            }}
                        />
                    </Grid>
                    <Grid item marginBottom={isWrapped ? 1 : 2} xs={isWrapped ? 12 : 3.5}>
                        <LabeledButton
                            className={classes.keepMePostedButton}
                            color="primary"
                            disabled={isLoading}
                            id="keepMePosted"
                            labelText={t("home:keepMePosted")}
                            type="submit"
                        >
                            {t("home:keepMePosted")}
                        </LabeledButton>
                    </Grid>
                </Grid>
            </form>
        ),
        [
            classes.keepMePostedButton,
            formMethods.control,
            formMethods.errors.email,
            isLoading,
            isWrapped,
            submitEmail,
            t,
        ]
    );

    return (
        <Card className={classes.card} elevation={2}>
            <CardContent>
                {isEmailSentSucceed ? renderSuccessContent() : renderFormContent()}
                {error && (
                    <Box paddingTop={4}>
                        <Error error={error} />
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};
